import { Building2, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import {
  isProjectVersion,
  versionName,
  type Project,
} from '@obraplan/core/domain/structure/project';
import type { SupplyProject } from '@obraplan/core/application/ports/requisition';
import { SupplyProjectField } from '@/features/projects/SupplyProjectField';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Section } from '@/components/ui/section';
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { EmptyState, LoadingState } from '@/components/feedback/states';
import { ProjectEditDialog, type ProjectFormValues } from '@/features/projects/ProjectEditDialog';
import { ApproveVersionDialog } from '@/features/projects/ApproveVersionDialog';
import { DuplicateProjectDialog } from '@/features/projects/DuplicateProjectDialog';
import { groupProjectFamilies, type ProjectFamily } from '@/features/projects/project-families';
import { ProjectRow } from '@/features/projects/ProjectRow';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/** O que ainda está no dia a dia do canteiro — o resto é história, e fica em outro bloco. */
const CURRENT_STATUSES = new Set<Project['status']>(['PLANNING', 'ACTIVE', 'PAUSED']);

const formSchema = z.object({
  code: z.string().trim().min(1, 'Código é obrigatório.').max(30),
  name: z.string().trim().min(1, 'Nome é obrigatório.').max(160),
  client: z.string().trim().max(160).optional(),
  externalId: z.string().trim().max(64).optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function ProjectsPage() {
  const container = useContainer();
  const { session, selectProject, activeProjectId } = useSessionState();
  const canManage = usePermission(PERMISSION.PROJECTS_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Project | undefined>(undefined);
  const [archiveTarget, setArchiveTarget] = useState<Project | undefined>(undefined);
  // Versões de orçamento (07/10/2026): duplicar, aprovar e excluir.
  const [duplicateTarget, setDuplicateTarget] = useState<Project | undefined>(undefined);
  const [createdVersion, setCreatedVersion] = useState<Project | undefined>(undefined);
  const [approveTarget, setApproveTarget] = useState<Project | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Project | undefined>(undefined);

  const projects = useLive(
    () =>
      session === undefined
        ? Promise.resolve([])
        : container.listAccessibleProjects.execute(session),
    [session?.userId],
  );

  // O prazo de cada obra é lido contra o hoje **dela** (RN-123): duas obras em fusos
  // diferentes podem estar em dias diferentes na mesma hora.
  const todayOf = useMemo(() => {
    const cache = new Map<string, string>();
    return (timezone: string): string => {
      const cached = cache.get(timezone);
      if (cached !== undefined) return cached;
      const today = container.clock.today(timezone).toString();
      cache.set(timezone, today);
      return today;
    };
  }, [container]);

  // A obra aprovada e as versões dela andam juntas: a família vai para o bloco da aprovada,
  // seja qual for a situação de cada versão.
  const families = groupProjectFamilies(projects ?? []);
  const current = families.filter((family) => CURRENT_STATUSES.has(family.root.status));
  const closed = families.filter((family) => !CURRENT_STATUSES.has(family.root.status));

  const renderRow = (project: Project, nested: boolean, hasVersions: boolean) => (
    <ProjectRow
      key={project.id}
      project={project}
      today={todayOf(project.timezone)}
      isActive={activeProjectId === project.id}
      canManage={canManage}
      nested={nested}
      hasVersions={hasVersions}
      onSelect={() => void selectProject(project.id)}
      onEdit={() => setEditTarget(project)}
      onArchive={() => setArchiveTarget(project)}
      onDuplicate={() => setDuplicateTarget(project)}
      onApprove={() => setApproveTarget(project)}
      onDeleteVersion={() => setDeleteTarget(project)}
    />
  );

  const renderFamily = (family: ProjectFamily) => [
    renderRow(family.root, false, family.versions.length > 0),
    ...family.versions.map((version) => renderRow(version, true, false)),
  ];

  /**
   * O nome que a versão nasce: o da aprovada da família e o próximo número. O servidor dá
   * o número de verdade; aqui é só a sugestão, que a pessoa pode trocar.
   */
  const suggestedVersionName = (source: Project | undefined): string => {
    if (source === undefined) return '';
    const approved = isProjectVersion(source)
      ? (projects ?? []).find((project) => project.id === source.versionOf)
      : source;
    const approvedId = approved?.id ?? source.versionOf;
    const numbers = (projects ?? [])
      .filter((project) => project.versionOf !== undefined && project.versionOf === approvedId)
      .map((project) => project.versionNumber ?? 1);
    const next = Math.max(1, ...numbers) + 1;
    return versionName(approved?.name ?? source.name, next);
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { code: '', name: '', client: '', externalId: '' },
  });

  /**
   * Escolher o destino preenche código e nome que ainda estiverem em branco — não
   * sobrescreve o que já foi digitado. O cliente não é sugerido: a lista traz quem
   * incorpora, que nem sempre é o cliente da obra.
   */
  const sugerirDoDestino = useCallback(
    (destino: SupplyProject | undefined) => {
      if (destino === undefined) return;
      if (form.getValues('code') === '' && destino.code !== undefined) {
        form.setValue('code', destino.code, { shouldValidate: true });
      }
      if (form.getValues('name') === '') {
        form.setValue('name', destino.name, { shouldValidate: true });
      }
    },
    [form],
  );

  const [createProject, createState] = useCommand(
    (values: FormValues) =>
      container.createProject.execute(
        {
          ...values,
          // Campo em branco é ausência, não string vazia: o contrato recusa o vazio, e
          // gravá-lo faria uma obra sem vínculo parecer uma obra vinculada a nada.
          client: values.client === '' ? undefined : values.client,
          externalId: values.externalId === '' ? undefined : values.externalId,
        },
        session!,
      ),
    {
      successMessage: 'Obra criada.',
      silent: true,
      onSuccess: async (project) => {
        form.reset();
        setCreateOpen(false);
        // Entrar na obra recém-criada é o próximo passo óbvio: montar a estrutura.
        await selectProject(project.id);
      },
    },
  );

  const [updateProject, updateState] = useCommand(
    (project: Project, values: ProjectFormValues) =>
      container.updateProject.execute(
        {
          projectId: project.id,
          // O fuso é da obra e não é editável por aqui: mudá-lo depois moveria o "hoje"
          // de todo o histórico já registrado (RN-123).
          timezone: project.timezone,
          name: values.name,
          client: values.client === '' ? undefined : values.client,
          address: values.address === '' ? undefined : values.address,
          technicalManager: values.technicalManager === '' ? undefined : values.technicalManager,
          status: values.status,
          startDate: values.startDate === '' ? undefined : values.startDate,
          endDate: values.endDate === '' ? undefined : values.endDate,
          riskHorizonDays: values.riskHorizonDays,
          requireDistinctInspector: values.requireDistinctInspector,
          measurementMode: values.measurementMode,
          // Vai sempre, inclusive vazio: aqui o vazio **desliga** a integração, e omitir
          // o campo faria o caso de uso manter o destino antigo — a opção "não requisita
          // material" não teria efeito nenhum.
          externalId: values.externalId,
        },
        session!,
      ),
    { successMessage: 'Obra atualizada.', silent: true, onSuccess: () => setEditTarget(undefined) },
  );

  const [archiveProject] = useCommand(
    (project: Project) =>
      container.updateProject.execute(
        {
          projectId: project.id,
          timezone: project.timezone,
          name: project.name,
          client: project.client,
          address: project.address,
          // Arquivar reaproveita o mesmo caso de uso de editar, e nele o campo ausente
          // **apaga**. Cada texto livre da obra precisa ser reenviado como está, ou
          // arquivar viraria um jeito silencioso de esvaziar o cadastro.
          technicalManager: project.technicalManager,
          status: 'ARCHIVED',
          startDate: project.startDate,
          endDate: project.endDate,
          riskHorizonDays: project.riskHorizonDays,
          requireDistinctInspector: project.requireDistinctInspector,
        },
        session!,
      ),
    {
      successMessage: 'Obra arquivada.',
      onSuccess: async (project) => {
        setArchiveTarget(undefined);
        // Continuar "dentro" de uma obra arquivada é convite a lançar dado nela.
        if (activeProjectId === project.id) await selectProject(undefined);
      },
    },
  );

  const [duplicateProject, duplicateState] = useCommand(
    (source: Project, name: string) =>
      container.duplicateProject.execute(
        { projectId: source.id, ...(name === '' ? {} : { name }) },
        session!,
      ),
    { silent: true, onSuccess: (version) => setCreatedVersion(version) },
  );

  const [deleteVersion] = useCommand(
    (version: Project) =>
      container.deleteProjectVersion.execute({ projectId: version.id }, session!),
    {
      successMessage: 'Versão excluída.',
      onSuccess: async () => {
        const excluded = deleteTarget;
        setDeleteTarget(undefined);
        // Continuar "dentro" de uma obra que não existe mais é convite a lançar dado nela.
        if (excluded !== undefined && activeProjectId === excluded.id) {
          await selectProject(excluded.versionOf);
        }
      },
    },
  );

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Obras</h1>
          <p className="text-(--color-content-muted)">
            Cada obra tem sua própria estrutura, cronograma e equipe.
          </p>
        </div>
        {canManage && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden="true" />
            Nova obra
          </Button>
        )}
      </header>

      {projects === undefined ? (
        <LoadingState />
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            title="Nenhuma obra cadastrada"
            description="Crie a primeira obra para começar a montar a estrutura."
            icon={<Building2 className="size-8" />}
            {...(canManage
              ? { action: { label: 'Nova obra', onClick: () => setCreateOpen(true) } }
              : {})}
          />
        </Card>
      ) : (
        <>
          <Section
            title="Em andamento"
            count={current.length}
            description="Em planejamento, ativas ou pausadas — as que ainda recebem lançamento."
          >
            {current.length === 0 ? (
              <p className="text-sm text-(--color-content-muted)">
                Nenhuma obra em andamento. As encerradas continuam abaixo.
              </p>
            ) : (
              <Card>
                <ul className="divide-y divide-(--color-border-subtle)">
                  {current.flatMap(renderFamily)}
                </ul>
              </Card>
            )}
          </Section>

          {closed.length > 0 && (
            <Section
              title="Encerradas"
              count={closed.length}
              description="Concluídas e arquivadas. Nada foi apagado: para voltar ao dia a dia, edite a situação."
            >
              <Card>
                <ul className="divide-y divide-(--color-border-subtle)">
                  {closed.flatMap(renderFamily)}
                </ul>
              </Card>
            </Section>
          )}
        </>
      )}

      <ProjectEditDialog
        open={editTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditTarget(undefined);
        }}
        project={editTarget}
        onSubmit={(values) => {
          if (editTarget !== undefined) void updateProject(editTarget, values);
        }}
        isPending={updateState.isPending}
        errorMessage={updateState.error?.message}
      />

      <ConfirmDialog
        open={archiveTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(undefined);
        }}
        destructive
        title={`Arquivar ${archiveTarget?.name ?? ''}?`}
        description="A obra sai do dia a dia, mas nada é apagado — para voltar, edite a situação."
        confirmLabel="Arquivar"
        onConfirm={() => {
          if (archiveTarget !== undefined) void archiveProject(archiveTarget);
        }}
      />

      <DuplicateProjectDialog
        open={duplicateTarget !== undefined}
        onOpenChange={(open) => {
          if (open) return;
          setDuplicateTarget(undefined);
          setCreatedVersion(undefined);
        }}
        source={duplicateTarget}
        suggestedName={suggestedVersionName(duplicateTarget)}
        onSubmit={(name) => {
          if (duplicateTarget !== undefined) void duplicateProject(duplicateTarget, name);
        }}
        isPending={duplicateState.isPending}
        errorMessage={duplicateState.error?.message}
        created={createdVersion}
        onOpenCreated={(version) => {
          setDuplicateTarget(undefined);
          setCreatedVersion(undefined);
          void selectProject(version.id);
        }}
      />

      {/* A aprovação leva a operação da aprovada de hoje, com a prévia e as escolhas (Fase 36). */}
      <ApproveVersionDialog
        version={approveTarget}
        onClose={() => setApproveTarget(undefined)}
        onApproved={() => setApproveTarget(undefined)}
      />

      <ConfirmDialog
        open={deleteTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(undefined);
        }}
        destructive
        title={`Excluir ${deleteTarget?.name ?? ''}?`}
        description="Apaga o projeto e o orçamento copiados para esta versão. A obra aprovada e as outras versões não são afetadas. Não há como desfazer."
        confirmLabel="Excluir versão"
        onConfirm={() => {
          if (deleteTarget !== undefined) void deleteVersion(deleteTarget);
        }}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova obra</DialogTitle>
            <DialogDescription>
              O código é curto e identifica a obra no sistema — ex.: MONTREZOR.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit((values) => createProject(values))(event);
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Código"
              htmlFor="project-code"
              required
              error={
                form.formState.errors.code?.message ??
                (createState.error?.kind === 'conflict' ? createState.error.message : undefined)
              }
            >
              <Input id="project-code" autoFocus {...form.register('code')} />
            </Field>
            <Field
              label="Nome"
              htmlFor="project-name"
              required
              error={form.formState.errors.name?.message}
            >
              <Input id="project-name" {...form.register('name')} />
            </Field>
            <Field label="Cliente" htmlFor="project-client">
              <Input id="project-client" {...form.register('client')} />
            </Field>
            <Field
              label="Destino no sistema de compras"
              htmlFor="project-external-id"
              hint="Projeto que recebe as requisições desta obra."
            >
              <SupplyProjectField
                id="project-external-id"
                registration={form.register('externalId')}
                onSelect={sugerirDoDestino}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={createState.isPending}>
                {createState.isPending ? 'Criando…' : 'Criar obra'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
